// SPDX-License-Identifier: AGPL-3.0-or-later
import { ExtensionUrl, findExtension, } from '@intakeform/fhir';
import { allAnswers, indexResponseItems, projectSingleAnswer, walkQuestionnaireItems, } from './answers.js';
import { isDateOfBirthItem, runDobChecks } from './dob.js';
import { parseAndValidatePhone } from './phone.js';
/**
 * Field-level validation pass. For each *active* item, checks (in order):
 *
 * 1. type coercion — the FHIR value[x] key matches `item.type`
 * 2. required — at least one non-empty answer
 * 3. regex extension — every string answer matches
 * 4. minValue / maxValue extensions — every numeric/date answer is in range
 *
 * Custom user-facing messages are sourced from the `validation-message`
 * extension when present.
 */
export function runFieldPass(questionnaire, response, activeLinkIds, options = {}) {
    const now = options.now ?? new Date();
    const findings = [];
    const responseItems = indexResponseItems(response);
    for (const item of walkQuestionnaireItems(questionnaire.item)) {
        const linkId = item.linkId;
        if (!activeLinkIds.has(linkId))
            continue;
        const responseItem = responseItems.get(linkId);
        const answers = responseItem?.answer ?? [];
        // 2. required
        if (item.required === true) {
            const hasAnswer = answers.some((a) => projectSingleAnswer(a) !== undefined);
            if (!hasAnswer) {
                findings.push(buildFinding(item, 'required', defaultMessage('required', item)));
                continue; // no point running the other checks on a missing field
            }
        }
        if (answers.length === 0)
            continue;
        // 1. type coercion — every answer must use the value[x] key expected by item.type
        for (const a of answers) {
            if (!matchesItemType(item.type, a)) {
                findings.push(buildFinding(item, 'type', defaultMessage('type', item), {
                    expected: item.type,
                }));
                break;
            }
        }
        const values = allAnswers(response, linkId);
        // 3. regex
        const regexExt = findExtension(item, ExtensionUrl.regex);
        const regexSource = regexExt?.valueString;
        if (regexSource !== undefined) {
            let re;
            try {
                re = new RegExp(regexSource, 'u');
            }
            catch (_err) {
                // A malformed pattern is a builder bug, not a patient bug — surface as form-level info.
                findings.push({
                    linkId: item.linkId,
                    severity: 'info',
                    code: 'regex',
                    message: `invalid regex on item '${item.linkId}'`,
                });
            }
            if (re !== undefined) {
                for (const v of values) {
                    if (typeof v !== 'string')
                        continue;
                    if (!re.test(v)) {
                        findings.push(buildFinding(item, 'regex', defaultMessage('regex', item)));
                        break;
                    }
                }
            }
        }
        // 3b. phone-validation (in-apm). Opt-in via the `phoneValidation`
        // extension; the parse target is the item's `phoneCountryBias`
        // override, falling back to the org default, then to 'US'. Empty
        // string answers are skipped — `required` already covers the
        // "patient didn't fill it in" path; running libphonenumber on
        // "" would just produce a redundant unparseable finding.
        if (findExtension(item, ExtensionUrl.phoneValidation)?.valueBoolean === true) {
            const bias = findExtension(item, ExtensionUrl.phoneCountryBias)?.valueCode ??
                options.defaultPhoneCountry ??
                'US';
            for (const v of values) {
                if (typeof v !== 'string' || v.trim() === '')
                    continue;
                const result = parseAndValidatePhone(v, bias);
                if (!result.ok) {
                    findings.push(buildFinding(item, result.reason === 'unparseable' ? 'phone-unparseable' : 'phone-invalid', result.message, { country: bias, input: v }));
                    break;
                }
            }
        }
        // 4. min / max
        const min = extractNumber(findExtension(item, ExtensionUrl.minValue));
        const max = extractNumber(findExtension(item, ExtensionUrl.maxValue));
        if (min !== undefined) {
            for (const v of values) {
                const n = numericOf(v);
                if (n !== undefined && n < min) {
                    // `n` in context is the threshold the UI translator
                    // interpolates into `finding.min`. `min`/`actual` retained
                    // for telemetry + dashboards.
                    findings.push(buildFinding(item, 'min', defaultMessage('min', item), {
                        n: min,
                        min,
                        actual: n,
                    }));
                    break;
                }
            }
        }
        if (max !== undefined) {
            for (const v of values) {
                const n = numericOf(v);
                if (n !== undefined && n > max) {
                    findings.push(buildFinding(item, 'max', defaultMessage('max', item), {
                        n: max,
                        max,
                        actual: n,
                    }));
                    break;
                }
            }
        }
        // 5. date-of-birth sanity (in-3uo) — only when the item carries
        //    the `date-mode='date-of-birth'` extension.
        if (isDateOfBirthItem(item)) {
            for (const a of answers) {
                const raw = a.valueDate;
                if (typeof raw !== 'string')
                    continue;
                for (const f of runDobChecks(item, raw, now))
                    findings.push(f);
            }
        }
    }
    return findings;
}
function matchesItemType(type, answer) {
    switch (type) {
        case 'boolean':
            return answer.valueBoolean !== undefined;
        case 'decimal':
            return answer.valueDecimal !== undefined || answer.valueInteger !== undefined;
        case 'integer':
            return answer.valueInteger !== undefined;
        case 'date':
            return answer.valueDate !== undefined;
        case 'dateTime':
            return answer.valueDateTime !== undefined;
        case 'time':
            return answer.valueTime !== undefined;
        case 'string':
            return answer.valueString !== undefined;
        case 'text':
            return answer.valueString !== undefined;
        case 'url':
            return answer.valueUri !== undefined || answer.valueString !== undefined;
        case 'choice':
        case 'open-choice':
            return (answer.valueCoding !== undefined ||
                answer.valueString !== undefined ||
                answer.valueInteger !== undefined ||
                answer.valueDate !== undefined ||
                answer.valueTime !== undefined);
        case 'attachment':
            return answer.valueAttachment !== undefined;
        case 'reference':
            return true;
        case 'quantity':
            return true;
        case 'group':
        case 'display':
            return true;
    }
}
function numericOf(v) {
    if (typeof v === 'number')
        return v;
    if (typeof v === 'string') {
        if (/^\d{4}(-\d{2}(-\d{2})?)?$/u.test(v)) {
            // For date strings, compare lexicographically by using parsed epoch.
            const parsed = Date.parse(v);
            return Number.isNaN(parsed) ? undefined : parsed;
        }
        const n = Number(v);
        return Number.isNaN(n) ? undefined : n;
    }
    return undefined;
}
function extractNumber(ext) {
    if (ext === undefined)
        return undefined;
    if (ext.valueDecimal !== undefined)
        return ext.valueDecimal;
    if (ext.valueInteger !== undefined)
        return ext.valueInteger;
    if (ext.valueDate !== undefined) {
        const parsed = Date.parse(ext.valueDate);
        return Number.isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
}
function buildFinding(item, code, message, context) {
    // Set `i18nKey` so the UI's FormError translator looks the
    // validator's findings up in the chrome dictionary. Custom
    // messages from the `validation-message` extension override the
    // default English here but the i18nKey still drives the
    // translation — clinicians who want a fully custom (untranslated)
    // string should leave i18nKey unset, which means building the
    // Finding outside `buildFinding`.
    return {
        linkId: item.linkId,
        severity: 'error',
        code,
        message,
        context,
        i18nKey: `finding.${code}`,
    };
}
function defaultMessage(code, item) {
    const custom = findExtension(item, ExtensionUrl.validationMessage)?.valueString;
    if (custom !== undefined)
        return custom;
    switch (code) {
        case 'required':
            return `This field is required.`;
        case 'type':
            return `Answer does not match the expected type (${item.type}).`;
        case 'regex':
            return `Answer does not match the required format.`;
        case 'min':
            return `Answer is below the allowed minimum.`;
        case 'max':
            return `Answer is above the allowed maximum.`;
        default:
            return `Validation failed.`;
    }
}
//# sourceMappingURL=field-validators.js.map